/**
 * COUNTING THINGS IN A LANGUAGE THAT HAS A DUAL.
 *
 * There is no plural machinery in this i18n runtime — deliberately, it is a
 * flat key/value map — so every counted noun is spelled as separate keys. That
 * has bitten this product twice, and the second time is why this file exists:
 *
 *  · Unit 5's browser round found **"1 deliveries"** (B67). The fix there was a
 *    `…One` key beside the general one, and that is what every counted noun in
 *    this app has carried since.
 *  · Unit 4's round found **«2 أصناف»** on the split sheet. A `…One` key is not
 *    enough for Arabic: two of something is the DUAL — «صنفين», «فاتورتين» —
 *    and a bare number with the plural is as wrong there as "1 bills" is in
 *    English. Three screens had it, because each one followed the pattern the
 *    first round established without anybody noticing the pattern was short.
 *
 * So the rule lives HERE, once, rather than as a ternary repeated at each call
 * site: three of those ternaries were what made one missing case into three
 * defects. English simply spells its `…Two` key the same as the general form,
 * which costs one line per noun and makes the shape identical in both
 * catalogues.
 *
 *  · B118's round table found the third case, and found the catalogue already
 *    split down the middle about it. This file used to say 3–10 and 11+ were
 *    "deliberately NOT modelled" because "the copy here is Egyptian colloquial,
 *    which levels those". It does not level them. Egyptian colloquial keeps the
 *    break exactly where the grammar puts it: **3–10 take the plural** («تلات
 *    أصناف»), **11 and up take the singular** («١٢ صنف»). Saying «٣ صنف» is as
 *    wrong on a till as «٢ أصناف» was, and «١٢ أصناف» is wrong the other way.
 *
 *    The proof it was never levelled is in the catalogue itself: half the
 *    counted nouns in `messages.ts` spelled their general form with the plural
 *    («في {count} أصناف محدش خدها») and half with the singular («{count} صنف»),
 *    so on one screen a party of five read correctly and a bill of twelve read
 *    wrong, and on the next screen it was the other way round. One rule, two
 *    hand-kept halves.
 *
 * `few` is OPTIONAL, and that is not laziness. English needs no third form, and
 * neither does an Arabic string that spells no noun — `summaryOpen` is «{count}
 * مفتوحة» with the table elided, and the adjective does not move between three
 * and thirty. Omitting it there says "this noun has no third form", which is a
 * fact about the string; requiring it everywhere would force fifteen copies of
 * a distinction twelve of them make and three do not.
 */

export type CountedKeys<T> = {
  /** Shown when the count is 0. Omit when zero cannot happen. */
  none?: T;
  one: T;
  /** The DUAL. In English, the same words as `many`. */
  two: T;
  /**
   * 3–10, which Arabic spells with the PLURAL. Takes `{count}`. Omit where the
   * string spells no noun that moves, and `many` serves the whole range; in
   * English it is always the same words as `many`.
   */
  few?: T;
  /** 11 and up, which Arabic spells with the SINGULAR. Takes `{count}`. */
  many: T;
};

/**
 * Picks the key for `count`. The caller passes the keys it already has; this
 * decides which, so no screen decides it twice.
 *
 * A NEGATIVE count falls to `many`, deliberately: nothing counted on these
 * screens can be negative, and inventing a case for it would be a fifth arm
 * every caller pays for and none can produce.
 */
export function countedKey<T>(count: number, keys: CountedKeys<T>): T {
  if (count === 0 && keys.none !== undefined) return keys.none;
  if (count === 1) return keys.one;
  if (count === 2) return keys.two;
  if (count >= 3 && count <= 10 && keys.few !== undefined) return keys.few;
  return keys.many;
}
